import { WebViewerInstance } from '@pdftron/webviewer'

import colors from '~/styles/_colors.css'

import { getHeaderItem, zoomIn, zoomOut } from './pdftronUtils'
import {
    IM_DFM_ANNOTATE_TOOL_BUTTONS,
    DISABLED_TOOLS,
    PrintButton,
    ZoomInButton,
    ZoomOutButton,
} from './constants'

import { PDFViewerProps } from './index'

const DISABLED_GROUPS = [
    'toolbarGroup-Edit',
    'toolbarGroup-Insert',
    'toolbarGroup-Measure',
    'toolbarGroup-Shapes',
    'toolbarGroup-View',
]

const DISABLED_ELEMENTS = [
    'signaturePanelButton',
    'opacitySlider',
    'linkButton',
    'ribbons',
    'menuButton',
]

const configureToolbars = (
    instance: WebViewerInstance,
    props: PDFViewerProps,
    defautHeaders: any,
): void => {
    // List of tools for Editor user
    const FitMode = instance.FitMode

    instance.setFitMode(FitMode.FitPage)

    // Disable all other toolbars, we will use just the annotate toolbar for now
    instance.disableElements(DISABLED_GROUPS)

    // Disable the ribbons to hide the toolbar selection buttons
    instance.disableElements([
        'ribbons',
        'printButton',
        'zoomInButton',
        'zoomOutButton',
    ])

    // Set Annotate toolbar as default
    instance.setToolbarGroup('toolbarGroup-Annotate', true)

    // Disable features we do not want
    instance.disableFeatures([
        instance.Feature.Measurement,
        instance.Feature.NotesPanel,
    ])

    // Set tools in Annotate header
    instance.setHeaderItems(header => {
        const headerItems = defautHeaders

        const leftPanelButton = getHeaderItem(headerItems, 'leftPanelButton')
        const viewControlsButton = getHeaderItem(
            headerItems,
            'viewControlsButton',
        )
        const zoomOverlayButton = getHeaderItem(
            headerItems,
            'zoomOverlayButton',
        )

        const panToolButton = {
            type: 'toolButton',
            toolName: 'Pan',
            dataElement: 'panToolButton',
        }
        const selectToolButton = {
            type: 'toolButton',
            toolName: 'AnnotationEdit',
            dataElement: 'selectToolButton',
        }
        const searchButton = getHeaderItem(headerItems, 'searchButton')
        const toggleNotesButton = getHeaderItem(
            headerItems,
            'toggleNotesButton',
        )
        const menuButton = getHeaderItem(headerItems, 'menuButton')

        const printButton = {
            ...PrintButton,
            onClick: () => {
                instance.print()
            },
        }

        const zoomInButton = {
            ...ZoomInButton,
            onClick: () => zoomIn(instance),
        }

        const zoomOutButton = {
            ...ZoomOutButton,
            onClick: () => zoomOut(instance),
        }

        header.update([
            leftPanelButton,
            {
                type: 'spacer',
            },
            viewControlsButton,
            zoomOverlayButton,
            zoomOutButton,
            zoomInButton,
            {
                type: 'divider',
            },
            panToolButton,
            selectToolButton,
            {
                type: 'spacer',
            },
            searchButton,
            toggleNotesButton,
            menuButton,
            printButton,
        ])

        const annotateHeader = header.getHeader('toolbarGroup-Annotate')

        const annotateHeaderItems = annotateHeader.getItems()

        // Grab undo/redo buttons from annotate bar since they have custom functions assigned
        const undoButton = getHeaderItem(annotateHeaderItems, 'undoButton')
        const redoButton = getHeaderItem(annotateHeaderItems, 'redoButton')
        const toolsOverlay = getHeaderItem(annotateHeaderItems, 'toolsOverlay')

        const EDITOR_TOOL_BUTTONS_WITH_PRESETS =
            IM_DFM_ANNOTATE_TOOL_BUTTONS.map(buttonItem => {
                return {
                    ...buttonItem,
                    showPresets: true,
                }
            })

        // Configure eraser tool button
        const eraserButton = {
            toolName: 'AnnotationEraserTool',
            type: 'toolButton',
        }

        // Write updated tool list to Annotate header
        annotateHeader.update([
            {
                type: 'spacer',
            },
            ...EDITOR_TOOL_BUTTONS_WITH_PRESETS,
            {
                type: 'divider',
            },
            toolsOverlay,
            undoButton,
            redoButton,
            eraserButton,
            {
                type: 'spacer',
            },
        ])
    })
}

const configureTools = (instance: WebViewerInstance): void => {
    // Disable tools we do not want
    instance.disableTools(DISABLED_TOOLS)

    // Disable elements
    instance.disableElements(DISABLED_ELEMENTS)

    // Disable reply for comments
    instance.disableReplyForAnnotations(() => true)

    // Stroke thickness
    const { docViewer, Annotations } = instance

    const NO_BORDER = 0
    const RED = new Annotations.Color(245, 74, 74)
    const STROKE_THICKNESS = 2
    const STROKE_STYLE = {
        StrokeColor: RED,
        StrokeThickness: STROKE_THICKNESS,
        Style: 'solid',
    }

    docViewer.getTool('AnnotationCreateArrow').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreatePolyline').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreatePolygon').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreateCallout').setStyles(() => ({
        StrokeColor: RED,
        StrokeThickness: STROKE_THICKNESS,
        TextColor: RED,
        FontSize: '16pt',
        Style: 'solid',
    }))

    docViewer.getTool('AnnotationCreateEllipse').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreateFreeText').setStyles(() => ({
        StrokeColor: RED,
        StrokeThickness: NO_BORDER,
        TextColor: RED,
        FontSize: '18pt',
        Style: 'solid',
    }))

    docViewer.getTool('AnnotationCreateLine').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreateRectangle').setStyles(() => STROKE_STYLE)

    docViewer.getTool('AnnotationCreateTextStrikeout').setStyles(() => ({
        StrokeColor: RED,
        StrokeThickness: 4,
        Style: 'solid',
    }))

    // callout enable auto sized
    const tool: any = instance.docViewer.getTool(
        instance.Tools.ToolNames.CALLOUT,
    )

    tool.setEnableAutoSized(true)
}

const configureColorPalette = (instance: WebViewerInstance): void => {
    instance.setColorPalette([colors.error, 'transparency'])
}

const apply = (
    instance: WebViewerInstance,
    props: PDFViewerProps,
    defautHeaders: object[] | null,
) => {
    // Set default color palette for all tools
    configureToolbars(instance, props, defautHeaders)

    // Set default color palette for all tools
    configureTools(instance)

    // Set default color palette for all tools
    configureColorPalette(instance)

    // Put WebViewer into readonly mode:
    // Annotations cannot be created, modified, or deleted.
    // No replies can be added to any annotation messages.
    instance.annotManager.setReadOnly(false)

    // Make customer annotations editable by admin
    instance.annotManager.setIsAdminUser(true)
}

export default apply
